import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import {
  CHECKBOX_POSITIONS,
  FIELD_POSITIONS,
  MAX_RECEIPT_ROWS,
  PAGE_HEIGHT,
  PAGE_WIDTH,
  PURPOSE_LINE_WIDTHS,
  RECEIPT_COLUMNS,
  receiptRowBaselineY,
} from './reimbursementLayout';

const TEMPLATE_URL = '/documents/reimbursement-form-template.pdf';
const INK = rgb(0.125, 0.157, 0.180); // theme brand.ink, close enough in grayscale-safe RGB for a printed form
const FIELD_FONT_SIZE = 10;
const TABLE_FONT_SIZE = 9;

// Truncates `text` (with a trailing ellipsis) so it fits within `maxWidth`
// points when drawn with `font` at `size`.
const truncateToWidth = (font, text, size, maxWidth) => {
  if (font.widthOfTextAtSize(text, size) <= maxWidth) return text;
  let truncated = text;
  while (truncated.length > 0 && font.widthOfTextAtSize(`${truncated}…`, size) > maxWidth) {
    truncated = truncated.slice(0, -1);
  }
  return `${truncated}…`;
};

// Greedily wraps `text` into at most `maxLines` lines, each no wider than
// `maxWidth`. Any remainder past `maxLines` is dropped (the template form
// only has room for two lines here).
const wrapToLines = (font, text, size, maxWidth, maxLines) => {
  const words = text.split(/\s+/).filter(Boolean);
  const lines = [];
  let current = '';

  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (font.widthOfTextAtSize(candidate, size) <= maxWidth) {
      current = candidate;
    } else {
      if (current) lines.push(current);
      current = word;
      if (lines.length === maxLines) break;
    }
  }
  if (current && lines.length < maxLines) lines.push(current);

  return lines.slice(0, maxLines);
};

const formatCurrency = (value) => {
  const amount = parseFloat(value);
  if (Number.isNaN(amount)) return '';
  return amount.toFixed(2);
};

/**
 * Loads the blank church reimbursement template and draws the submitted
 * form values on top of it, returning the filled PDF as bytes.
 */
export const fillReimbursementForm = async (formValues) => {
  const templateBytes = await fetch(TEMPLATE_URL).then((res) => res.arrayBuffer());
  const pdfDoc = await PDFDocument.load(templateBytes);
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const page = pdfDoc.getPage(0);

  const drawField = (key, text) => {
    if (!text) return;
    const { x, y } = FIELD_POSITIONS[key];
    page.drawText(text, { x, y, size: FIELD_FONT_SIZE, font, color: INK });
  };

  const drawCheckbox = (key, checked) => {
    if (!checked) return;
    const { x, y } = CHECKBOX_POSITIONS[key];
    page.drawText('X', { x, y, size: FIELD_FONT_SIZE, font, color: INK });
  };

  drawField('date', formValues.date);
  drawField('payableTo', formValues.payableTo);
  drawField('checkAmount', formatCurrency(formValues.checkAmount));
  drawField('phone', formValues.phone);
  drawField('needBy', formValues.needBy);
  drawField('requestedBy', formValues.requestedBy);
  drawField('approvedBy', formValues.approvedBy);
  drawField('grandTotal', formatCurrency(formValues.grandTotal));

  // The first purpose line sits on the same underline as the "Purpose/Reason
  // for Check Request:" label, so it has less room than the full-width
  // second line below it.
  const fullPurpose = formValues.purpose || '';
  const firstLine = wrapToLines(font, fullPurpose, FIELD_FONT_SIZE, PURPOSE_LINE_WIDTHS.purposeLine1, 1)[0] || '';
  drawField('purposeLine1', firstLine);
  const remainder = fullPurpose.slice(firstLine.length).trim();
  const secondLine = wrapToLines(font, remainder, FIELD_FONT_SIZE, PURPOSE_LINE_WIDTHS.purposeLine2, 1)[0];
  drawField('purposeLine2', secondLine);

  drawCheckbox('reimbursement', formValues.requestType === 'Reimbursement');
  drawCheckbox('checkRequest', formValues.requestType === 'Check Request');
  drawCheckbox('donation', formValues.requestType === 'Donation');
  drawCheckbox('boardMember', formValues.approvedByType === 'Board member');
  drawCheckbox('youthLeader', formValues.approvedByType === 'Youth Leader');
  drawCheckbox('other', formValues.approvedByType === 'Other');

  const receipts = (formValues.receipts || []).slice(0, MAX_RECEIPT_ROWS);
  receipts.forEach((receipt, index) => {
    const y = receiptRowBaselineY(index);

    if (receipt.purchasedFrom) {
      page.drawText(
        truncateToWidth(font, receipt.purchasedFrom, TABLE_FONT_SIZE, RECEIPT_COLUMNS.purchasedFrom.maxWidth),
        { x: RECEIPT_COLUMNS.purchasedFrom.x, y, size: TABLE_FONT_SIZE, font, color: INK }
      );
    }
    if (receipt.amount) {
      const amountText = formatCurrency(receipt.amount);
      const width = font.widthOfTextAtSize(amountText, TABLE_FONT_SIZE);
      page.drawText(amountText, {
        x: RECEIPT_COLUMNS.amount.rightX - width,
        y,
        size: TABLE_FONT_SIZE,
        font,
        color: INK,
      });
    }
    if (receipt.purpose) {
      page.drawText(
        truncateToWidth(font, receipt.purpose, TABLE_FONT_SIZE, RECEIPT_COLUMNS.purpose.maxWidth),
        { x: RECEIPT_COLUMNS.purpose.x, y, size: TABLE_FONT_SIZE, font, color: INK }
      );
    }
  });

  return pdfDoc.save();
};

// Adds a new Letter-sized page to `pdfDoc` with `imageBytes` scaled to fit
// (with a small margin) and centered.
const addImagePage = async (pdfDoc, imageBytes, mimeType) => {
  const image = mimeType === 'image/png'
    ? await pdfDoc.embedPng(imageBytes)
    : await pdfDoc.embedJpg(imageBytes);

  const margin = 36;
  const maxWidth = PAGE_WIDTH - margin * 2;
  const maxHeight = PAGE_HEIGHT - margin * 2;
  const scale = Math.min(maxWidth / image.width, maxHeight / image.height, 1);
  const drawWidth = image.width * scale;
  const drawHeight = image.height * scale;

  const page = pdfDoc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
  page.drawImage(image, {
    x: (PAGE_WIDTH - drawWidth) / 2,
    y: (PAGE_HEIGHT - drawHeight) / 2,
    width: drawWidth,
    height: drawHeight,
  });
};

const IMAGE_MIME_TYPES = new Set(['image/png', 'image/jpeg']);

/**
 * Builds the "complete packet": the filled form followed by every attached
 * receipt, each as its own page (PDF receipts are merged page-for-page;
 * images are placed one per page). Returns the combined PDF as bytes.
 */
export const buildReimbursementPacket = async (filledFormBytes, receiptFiles) => {
  const packet = await PDFDocument.create();
  const formDoc = await PDFDocument.load(filledFormBytes);
  const formPages = await packet.copyPages(formDoc, formDoc.getPageIndices());
  formPages.forEach((page) => packet.addPage(page));

  for (const file of receiptFiles) {
    const bytes = await file.arrayBuffer();
    if (file.type === 'application/pdf') {
      const receiptDoc = await PDFDocument.load(bytes);
      const pages = await packet.copyPages(receiptDoc, receiptDoc.getPageIndices());
      pages.forEach((page) => packet.addPage(page));
    } else if (IMAGE_MIME_TYPES.has(file.type)) {
      await addImagePage(packet, bytes, file.type);
    }
  }

  return packet.save();
};

export const downloadPdfBytes = (bytes, filename) => {
  const blob = new Blob([bytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
